import React from "react";
import { ChatBubbleBottomCenterIcon } from "@heroicons/react/24/outline";
import { ChevronDown, Users } from "lucide-react";
import CreateFolderModal from "./modals/createFolderModal";
import { UploadModal } from "./modals/uploadModal";
import { Button } from "@/components/ui/button";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { FolderItem } from "@/types/fse";
import { useNavigate } from "react-router";
import { DriveRowMenu } from "./components/driveRowMenu";
import { AddFilesModal } from "./modals/addFilesModal";
import { DriveHeaderBreadcrumbs } from "./driverHeaderBreadcrumbs";

interface DriveHeaderProps {
  parentFolder: FolderItem | null;
  currentFolder: FolderItem;
  askAIButton?: boolean;
  addFilesButton?: boolean;
}

export const DriveHeader: React.FC<DriveHeaderProps> = ({
  parentFolder,
  currentFolder,
  askAIButton,
  addFilesButton,
}) => {
  const folderName = currentFolder.name;
  const navigate = useNavigate();
  const authenticatedUser = useAuthenticatedUser();

  const isReadOnly = !!currentFolder.permissions.some(
    row => row.user_id === authenticatedUser.id && row.role === "viewer",
  );

  const handleAskAI = () => {
    navigate(`/chat/${currentFolder.id}`);
  };

  const onRemove = () => {
    navigate("/drive/");
  };

  const isShared = currentFolder.access === "public" || currentFolder.permissions.length > 1;

  return (
    <div className="mb-6 pb-4">
      <div className="mb-2 flex flex-col justify-between space-y-2 sm:flex-row sm:space-y-0">
        <h1 className="flex text-2xl font-bold">
          {folderName}

          {isShared && <Users className="ml-2 mt-2 h-4 w-4 text-gray-500" />}

          <DriveRowMenu
            isRootFolder={!(currentFolder.parent_fs_id && currentFolder.owner_id !== "system")}
            isFolder={true}
            variant="empty"
            item={currentFolder}
            onRemove={onRemove}
          >
            <ChevronDown className="h-4 w-4" />
          </DriveRowMenu>
        </h1>

        <div className="flex space-x-2 sm:space-x-4">
          {!!parentFolder && addFilesButton && (
            <AddFilesModal isReadOnly={isReadOnly} currentFolderId={currentFolder.id} />
          )}
          {!!parentFolder && askAIButton && (
            <Button variant="secondary" borderStyle="border-gray-300 border-solid" type="button" onClick={handleAskAI}>
              <ChatBubbleBottomCenterIcon className="mr-2 h-5 w-5" />
              Q&A
            </Button>
          )}
          <CreateFolderModal currentFolderId={currentFolder.id} isReadOnly={isReadOnly} />
          <UploadModal currentFolderId={currentFolder.id} isReadOnly={isReadOnly} />
        </div>
      </div>
      <DriveHeaderBreadcrumbs currentLocation={currentFolder.current_location} currentFolder={currentFolder} />
    </div>
  );
};
